<template>
  <div class="ahv-mailing">
    <form
      v-if="step === 'stepLogin'"
      novalidate
      class="sanitasForms"
      @submit.prevent="submitShortId"
    >
      <p v-text="$t('stepLoginIntroText')" />
      <div>
        <text-input
          v-model="shortId"
          :title-label="$t('stepLoginInputTitle')"
          placeholder="00000000"
          :max-length="8"
          :errors="shortIdErrors"
          name="shortid"
          autofocus
          required
        >
        </text-input>
      </div>
      <sanitas-button
        primary
        type="submit"
        :is-spinner-active="pendingNetworkCall"
        :disabled="pendingNetworkCall"
      >
        {{ $t('stepLoginWeiter') }}
      </sanitas-button>
    </form>
    <form
      v-if="step === 'stepForm'"
      novalidate
      class="sanitasForms"
      @submit.prevent="submitForm"
    >
      <h2 class="ahv-mailing__name">{{ partnerSalutationAndLastname }}</h2>
      <div class="ahv-mailing__section">
        <p v-text="$t('stepFormIntroText')" />
        <date-input
          v-model="stepForm.birthday"
          :title-label="$t('stepFormBirthday')"
          :errors="birthdayErrors"
          name="birthday"
          autofocus
          required
          :placeholder="$t('stepFormBirthdayPlaceholder')"
        />
      </div>
      <div class="ahv-mailing__section">
        <radio-group v-model="radioPreChoiceOk" :items="coverageRadio" />
        <div v-if="radioPreChoiceOk === 'no'">
          <p
            class="ahv-mailing__question"
            v-text="$t('stepFormWorkEndQuestion')"
          />
          <div class="ahv-mailing__date">
            <drop-down
              v-model="stepForm.changeDate"
              class="ahv-mailing__dropdown"
              :title-label="$t('stepFormDate')"
              :options="changeDateGenerated"
              :errors="changeDateErrors"
            >
            </drop-down>
            <Checkbox
              v-model="stepForm.coverageNotNeededYet"
              class="ahv-mailing__checkbox"
              :errors="coverageNotNeededYetError"
              :text="$t('stepFormDateNotYetKnown')"
            ></Checkbox>
          </div>
          <form-input-error :errors="dateChoiceErrors"></form-input-error>
          <p class="ahv-mailing__date-info" v-html="$t('stepFormDateInfo')" />
        </div>
      </div>
      <sanitas-button
        primary
        type="submit"
        :is-spinner-active="pendingNetworkCall"
        :disabled="pendingNetworkCall"
      >
        {{ $t('stepFormSubmit') }}
      </sanitas-button>
    </form>
    <div v-if="step === 'stepThankyou'">
      <h2 class="ahv-mailing__name">{{ partnerSalutationAndLastname }}</h2>
      <p
        v-if="loginData.status === 'ANSWERED' || saveStatus === true"
        v-text="$t('stepThankyouAlreadyText')"
      />
    </div>
    <alert-message
      v-if="alert"
      class="ahv-mailing__alert-message"
      :text="alert.text"
      :level="alert.level"
    />
  </div>
</template>
<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import { addMonths, formatISO, startOfMonth } from 'date-fns'
import { validationMixin } from 'vuelidate'
import { minLength, required, requiredIf } from 'vuelidate/lib/validators'

import Checkbox from '@/components/sanitas-components/Checkbox'
import DateInput from '@/components/sanitas-components/DateInput'
import DropDown from '@/components/sanitas-components/DropDown'
import FormInputError from '@/components/sanitas-components/FormInputError'
import RadioGroup from '@/components/sanitas-components/RadioGroup'
import SanitasButton from '@/components/sanitas-components/SanitasButton'
import TextInput from '@/components/sanitas-components/TextInput'
import { formatDate } from '@/helpers/date'
import { client } from '@/helpers/http'
import { isIsoDate } from '@/helpers/validation'

import AlertMessage from './components/AlertMessage'

const mustBeCodeRegEx = value => /^[0-9,A-F]{8}$/.test(value)
const mustBeIsoDate = value => isIsoDate(value)
export default {
  name: 'AhvMailing',
  components: {
    FormInputError,
    AlertMessage,
    SanitasButton,
    RadioGroup,
    DateInput,
    TextInput,
    Checkbox,
    DropDown,
  },
  mixins: [validationMixin],
  props: {
    language: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()
    return {
      $t: $label,
    }
  },
  data() {
    return {
      shortId: '',
      shortIdErrorShow: false,
      stepFormErrorShow: false,
      radioPreChoiceOk: 'yes',
      stepForm: {
        coverageNotNeededYet: false,
        changeDate: '',
        birthday: '',
      },
      coverageNotNeededYetError: {},
      loginData: null,
      saveStatus: false,
      partnerSalutationAndLastname: '',
      step: 'stepLogin',
      pendingNetworkCall: false,
      alert: null,
    }
  },
  validations() {
    return {
      shortId: {
        required,
        minLength: minLength(8),
        mustBeCodeRegEx,
      },
      stepForm: {
        birthday: {
          required,
          mustBeIsoDate,
        },
        coverageNotNeededYet: {
          required: requiredIf(() => {
            return this.coverageNotNeededYetRequired
          }),
        },
        changeDate: {
          required: requiredIf(() => {
            return this.changeDateRequired
          }),
          mustBeIsoDate,
        },
      },
    }
  },
  computed: {
    interactionPayload() {
      return {
        messageName: 'Message_Landing_Page',
        variables: {
          ...this.stepForm,
        },
      }
    },
    coverageRadio() {
      const dueDate = formatDate(this.loginData?.placeholders.dueDate)
      return [
        {
          text: `${this.$t('stepFormINeedCoverage')} ${dueDate}`,
          value: 'yes',
        },
        {
          text: this.$t('stepFormIDontNeedCoverageYet'),
          value: 'no',
        },
      ]
    },
    changeDateGenerated() {
      const nextDate = startOfMonth(new Date())
      let changeDateArray = []
      for (let i = 1; i < 13; i++) {
        changeDateArray.push({
          value: formatISO(addMonths(nextDate, i), { representation: 'date' }),
          name: formatDate(
            formatISO(addMonths(nextDate, i), { representation: 'date' }),
          ),
        })
      }
      return changeDateArray
    },
    baseDomain() {
      return process.env.VUE_APP_ROOT_API
    },
    shortIdErrors() {
      if (!this.shortIdErrorShow) {
        return {}
      }
      if (!this.$v.shortId.required) {
        return {
          message: this.$t('stepLoginValidationShortIdRequired'),
        }
      } else if (!this.$v.shortId.minLength) {
        return {
          message: this.$t('stepLoginValidationShortIdMinLength'),
        }
      } else if (!this.$v.shortId.mustBeCodeRegEx) {
        return {
          message: this.$t('stepLoginValidationShortIdFormat'),
        }
      } else {
        return {}
      }
    },
    birthdayErrors() {
      if (!this.stepFormErrorShow) {
        return {}
      }
      if (!this.$v.stepForm.birthday.required) {
        return {
          message: this.$t('stepFormValidationBirthdayRequired'),
        }
      } else if (!this.$v.stepForm.birthday.mustBeIsoDate) {
        return {
          message: this.$t('stepFormValidationBirthdayFormat'),
        }
      } else {
        return {}
      }
    },
    changeDateErrors() {
      // Show a red dropdown for the data choice error case
      return this.dateChoiceErrors.message ? { message: '' } : {}
    },
    dateChoiceErrors() {
      if (!this.stepFormErrorShow) {
        return {}
      }
      if (
        this.$v.stepForm.changeDate.$invalid ||
        this.$v.stepForm.coverageNotNeededYet.$invalid
      ) {
        return {
          message: this.$t('stepFormValidationDateChoiceErrorText'),
        }
      } else {
        return {}
      }
    },
    coverageNotNeededYetRequired() {
      if (this.radioPreChoiceOk === 'no' && !this.stepForm.changeDate) {
        return true
      }
      return false
    },
    changeDateRequired() {
      if (
        this.radioPreChoiceOk === 'no' &&
        !this.stepForm.coverageNotNeededYet
      ) {
        return true
      }
      return false
    },
  },
  watch: {
    'shortId': function () {
      this.shortIdErrorShow = false
    },
    'stepForm.coverageNotNeededYet': function () {
      if (this.stepForm.coverageNotNeededYet === true) {
        this.stepForm.changeDate = null
      }
    },
    'stepForm.changeDate': function () {
      if (this.stepForm.changeDate != null) {
        this.stepForm.coverageNotNeededYet = false
      }
    },
    'radioPreChoiceOk': function () {
      if (this.radioPreChoiceOk === 'yes') {
        this.stepForm.coverageNotNeededYet = false
        this.stepForm.changeDate = null
      }
    },
    'step': function () {
      if (this.step === 'stepThankyou') {
        //show a aem content component, some magazin teasers
        const element = document.querySelector('body > div.reference.parbase')
        if (element) {
          element.style.visibility = 'visible'
        }
      }
    },
  },
  methods: {
    submitShortId() {
      this.shortIdErrorShow = true
      this.alert = null
      if (this.pendingNetworkCall || this.$v.shortId.$invalid) {
        return
      }

      this.pendingNetworkCall = true
      client()
        .get(`${this.baseDomain}/public/campaign/v1/mailings/${this.shortId}`)
        .then(({ data }) => {
          if (!data?.data) {
            this.alert = {
              level: 'error',
              text: this.$t('stepThankyouNetworkErrorText'),
            }
            return
          }
          this.loginData = data.data
          this.partnerSalutationAndLastname = `${data.data.placeholders.partnerSalutation} ${data.data.placeholders.partnerLastname}`
          this.step = 'stepThankyou'
          switch (this.loginData.status) {
            case 'EXPIRED':
              this.step = 'stepThankyou'
              this.alert = {
                level: 'warning',
                text: this.$t('stepThankyouFristText'),
              }
              break
            case 'ANSWERED':
              this.step = 'stepThankyou'
              break
            case 'UNANSWERED':
              this.step = 'stepForm'
              break
          }
        })
        .catch(error => {
          if (error.response) {
            this.alert = {
              level: 'error',
              text: this.$t('stepLoginError'),
            }
          } else {
            this.alert = {
              level: 'error',
              text: this.$t('stepThankyouNetworkErrorText'),
            }
          }
        })
        .finally(() => {
          this.pendingNetworkCall = false
        })
    },
    submitForm() {
      this.stepFormErrorShow = true
      this.alert = null
      if (this.pendingNetworkCall || this.$v.stepForm.$invalid) {
        return
      }
      this.pendingNetworkCall = true

      client()
        .post(
          `${this.baseDomain}/public/campaign/v1/interactions/${this.loginData.correlationId}`,
          this.interactionPayload,
        )
        .then(({ data }) => {
          if (!data?.data) {
            this.alert = {
              level: 'error',
              text: this.$t('stepThankyouNetworkErrorText'),
            }
            return
          }
          this.saveStatus = data.data.success
          this.step = 'stepThankyou'
          //200 OK
          //201 Message received
        })
        .catch(error => {
          if (error.response) {
            this.alert = {
              level: 'error',
              text: this.$t('stepThankyouErrorText'),
            }
          } else {
            this.alert = {
              level: 'error',
              text: this.$t('stepThankyouNetworkErrorText'),
            }
          }
        })
        .finally(() => {
          this.pendingNetworkCall = false
        })
    },
  },
}
</script>
<style lang="postcss" scoped>
.ahv-mailing {
  margin-top: var(--spacing-6);
  margin-bottom: var(--spacing-6);
}

.ahv-mailing__dropdown {
  width: 160px;
}

.ahv-mailing__name {
  font-weight: bold;
  margin-bottom: var(--spacing-6);
}

.ahv-mailing__date {
  display: flex;
  align-items: flex-end;
}

.ahv-mailing__date-info {
  margin-top: var(--spacing-4);
}

.ahv-mailing__question {
  margin-top: var(--spacing-4);
}

.ahv-mailing__alert-message {
  margin-top: var(--spacing-4);
}

.ahv-mailing__section {
  margin-bottom: var(--spacing-4);
}

.ahv-mailing__checkbox {
  margin-left: var(--spacing-6);
  padding-bottom: 5px;
}
</style>
